import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import {
  UsersThree, Diamond, Stack, Calculator, MegaphoneSimple,
  Sparkle, CircleNotch, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { toastApiError } from "@/lib/apiError";
import {
  BUSINESS_MODEL_FIELDS,
  countWords,
  parseBusinessModel,
  assembleBusinessModel,
} from "@/lib/businessModel";

/**
 * Structured 5-field editor for slide 5 (Business Model / "Business Strategy").
 *
 * Iter55 (Feb 2026). Mirrors `MarketSizeForm.jsx` in architecture:
 *   • local `useState` per field so trailing whitespace + special chars stick
 *     mid-typing (lessons from iter54 spacebar bug — see PRD.md)
 *   • external content changes reconcile via `lastEmittedRef`
 *   • assembles into a canonical content string that
 *     `pitch_business_model.parse_business_model` on the server + the
 *     preview + the PPTX exporter all consume without translation.
 *
 * Layout is FIXED per user spec — 5 rows in canonical order, cannot be
 * reordered from the UI. Only fonts + brand colour flex per deck.
 *
 * Per-field AI redraft:
 *   • POST /api/pitch/decks/{deckId}/business-model/redraft {field_key}
 *   • 1 credit, idempotent, backend handles refund on timeout/unavailable
 *   • On success, only the target field's local state is patched
 */

const ICON_MAP = {
  UsersThree, Diamond, Stack, Calculator, MegaphoneSimple,
};

const C = {
  border: "rgba(255,255,255,0.08)",
  ink: "#F5F7FA",
  muted: "rgba(255,255,255,0.55)",
  bg: "#0F1B22",
  bgSoft: "#132029",
  warn: "#F59E0B",
};

function parseToLocal(content) {
  const arr = parseBusinessModel(content || "");
  const out = {};
  for (const row of arr) out[row.key] = row.text;
  return out;
}

export default function BusinessModelForm({ content, onChange, primary, deckId }) {
  const [texts, setTexts] = useState(() => parseToLocal(content));
  const [redrafting, setRedrafting] = useState({});   // { field_key: true } while API in flight
  const lastEmittedRef = useRef(content || "");

  // Reconcile from external content changes (e.g. AI premium rewrite lands
  // a new content string). Only reset local state if the incoming content
  // is different from what we last emitted upward.
  useEffect(() => {
    if ((content || "") === lastEmittedRef.current) return;
    setTexts(parseToLocal(content));
    lastEmittedRef.current = content || "";
  }, [content]);

  const emit = useCallback((nextTexts) => {
    const assembled = assembleBusinessModel(nextTexts);
    lastEmittedRef.current = assembled;
    onChange(assembled);
  }, [onChange]);

  const update = useCallback((key, value) => {
    setTexts((prev) => {
      const next = { ...prev, [key]: value };
      emit(next);
      return next;
    });
  }, [emit]);

  /* Live-preview overflow measurement (iter68).
   *
   * The Business Model preview renders 5 rows inside a fixed-height flex
   * container. Row bodies are not clamped — instead an over-long
   * paragraph grows its row and can push subsequent rows past the
   * slide's bottom edge, where the outer aspect-ratio wrapper clips
   * them. A raw word cap ("Over 32 words — will truncate") is
   * misleading because the text isn't truncated in place; the row just
   * displaces its neighbours.
   *
   * We now check per-row whether the row's `offsetTop + offsetHeight`
   * exceeds the diagram wrapper's `clientHeight`. If it does, that row
   * (or one above it) is being clipped off the visible slide and we
   * surface the honest warning "Row is pushing off the slide — trim
   * for the cleanest fit".
   */
  const [overflowKeys, setOverflowKeys] = useState({});
  useLayoutEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    const measure = () => {
      const diagram = document.querySelector('[data-testid="business-model-diagram"]');
      if (!diagram) {
        setOverflowKeys((prev) => (Object.keys(prev).length ? {} : prev));
        return;
      }
      const diagramH = diagram.clientHeight;
      const next = {};
      for (const f of BUSINESS_MODEL_FIELDS) {
        const row = document.querySelector(`[data-testid="business-model-preview-row-${f.key}"]`);
        if (!row) continue;
        // offsetParent chain — walk up until we hit the diagram or exit.
        let top = 0;
        let el = row;
        while (el && el !== diagram) {
          top += el.offsetTop;
          el = el.offsetParent;
        }
        // If we walked off the tree without finding diagram, fall back
        // to bounding-rect comparison.
        if (el !== diagram) {
          const rowRect = row.getBoundingClientRect();
          const dRect = diagram.getBoundingClientRect();
          if (rowRect.bottom > dRect.bottom + 1) next[f.key] = true;
          continue;
        }
        const bottom = top + row.offsetHeight;
        if (bottom > diagramH + 1) next[f.key] = true;
      }
      setOverflowKeys((prev) => {
        const pk = Object.keys(prev).sort().join(",");
        const nk = Object.keys(next).sort().join(",");
        return pk === nk ? prev : next;
      });
    };
    raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(measure); });
    window.addEventListener("resize", measure);
    let mo = null;
    const previewRoot = document.querySelector('[data-testid="business-model-diagram"]');
    if (previewRoot && typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(measure);
      mo.observe(previewRoot, { childList: true, subtree: true, characterData: true });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", measure);
      if (mo) mo.disconnect();
    };
  }, [texts]);

  const runRedraft = async (field) => {
    if (redrafting[field.key]) return;
    setRedrafting((s) => ({ ...s, [field.key]: true }));
    // Idempotency-Key per click, cleared on retry — matches ai_draft pattern
    const idem = `bmredraft-${deckId}-${field.key}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      const { data } = await api.post(
        `/pitch/decks/${deckId}/business-model/redraft`,
        { field_key: field.key },
        { headers: { "Idempotency-Key": idem } },
      );
      const text = (data && data.text) || "";
      setTexts((prev) => {
        const next = { ...prev, [field.key]: text };
        emit(next);
        return next;
      });
      window.dispatchEvent(new Event("clinq:credits-changed"));
      toast.success(`${field.label} redrafted · 1 credit used`);
    } catch (err) {
      toastApiError(err, `Couldn't redraft ${field.label}`);
    } finally {
      setRedrafting((s) => {
        const n = { ...s };
        delete n[field.key];
        return n;
      });
    }
  };

  return (
    <div data-testid="business-model-form" className="space-y-4">
      <div className="p-3 rounded-md text-[12px] leading-relaxed"
        style={{ backgroundColor: `${primary}14`, border: `1px solid ${primary}33`, color: C.ink }}>
        <strong className="font-bold">Business Strategy</strong> uses a canonical 5-row layout — one paragraph per building block. Fill in what you know; each row has its own AI redraft (1 credit) if you&apos;d like Clinq to draft a first pass. Keep answers tight — a very long paragraph will push other rows off the visible slide, and we&apos;ll flag it when it does.
      </div>

      <div className="space-y-3">
        {BUSINESS_MODEL_FIELDS.map((f) => {
          const Icon = ICON_MAP[f.icon] || Diamond;
          const value = texts[f.key] || "";
          const words = countWords(value);
          const overflows = !!overflowKeys[f.key];
          const busy = !!redrafting[f.key];
          return (
            <div key={f.key}
              data-testid={`business-model-row-${f.key}`}
              className="p-4 rounded-lg"
              style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex items-center justify-center rounded-full shrink-0"
                  style={{ width: 32, height: 32, backgroundColor: primary, color: "#fff" }}>
                  <Icon size={16} weight="fill" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold" style={{ color: C.ink, fontSize: 13 }}>
                    {f.label}
                  </div>
                  <div className="text-[11px]" style={{ color: C.muted }}>
                    {f.subtitle}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`business-model-redraft-${f.key}`}
                  onClick={() => runRedraft(f)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.12em] transition-opacity disabled:opacity-60"
                  style={{
                    backgroundColor: `${primary}22`,
                    border: `1px solid ${primary}55`,
                    color: primary,
                  }}
                  title="AI redraft this field (1 credit)"
                >
                  {busy
                    ? <CircleNotch size={12} weight="bold" className="animate-spin" />
                    : <Sparkle size={12} weight="fill" />}
                  {busy ? "Drafting…" : "AI redraft"}
                </button>
              </div>
              <textarea
                data-testid={`business-model-input-${f.key}`}
                value={value}
                onChange={(e) => update(f.key, e.target.value)}
                placeholder={f.subtitle}
                rows={3}
                className="w-full px-3 py-2 rounded text-[13px] focus:outline-none leading-relaxed resize-y"
                style={{
                  backgroundColor: C.bg,
                  border: `1px solid ${overflows ? C.warn : C.border}`,
                  color: C.ink,
                  minHeight: 68,
                }}
              />
              <div className="mt-1.5 flex items-center justify-between text-[10.5px]"
                style={{ color: overflows ? C.warn : C.muted }}>
                <span>
                  {overflows && (
                    <span className="inline-flex items-center gap-1 mr-2"
                      data-testid={`business-model-overflow-warn-${f.key}`}>
                      <WarningCircle size={12} weight="fill" />
                      Row is pushing off the slide — trim for the cleanest fit.
                    </span>
                  )}
                </span>
                <span data-testid={`business-model-wordcount-${f.key}`}>
                  {words} words
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
